/* eslint-disable global-require */
import React, { useContext, useEffect } from 'react'
import { Head } from '@notino/react-toolkit/exports/Head'
import { useIntl } from 'react-intl'

// types
import { SalonDetail } from '../../../../types/types'

// utils
import { getEnvironmentAndLocaleSpecificShopsConfig } from '../../../../appDefaults'
import { isSalonNonHomeShopView, PAGE_LINKS, truncateString } from '../../../../utils/helper'
import { AppContext } from '../../../../utils/appProvider'

type Props = {
	salonSlug: string
	salon: SalonDetail
	salonServices: string[]
}

const SalonHead = (props: Props) => {
	const { salonSlug, salon, salonServices } = props
	const { locale, formatMessage } = useIntl()
	const { assetsPath, productionEnvironment } = useContext(AppContext)

	const salonCountryCode = salon.address?.countryCode
	const currentShop = getEnvironmentAndLocaleSpecificShopsConfig(productionEnvironment, locale)
	const currentPath = `${currentShop.url}${PAGE_LINKS['/salons/:salonSlug'](locale, salonSlug)}`
	const salonNonHomeShopView = isSalonNonHomeShopView(currentShop.countryCode, salonCountryCode)

	const pageTitle = formatMessage({ id: 'SEO - Salon detail page - meta title' }, { salonName: salon.name, salonCity: salon.address?.city ?? '' })
	const description = formatMessage({ id: 'SEO - Salon detail page - meta description' }, { salonAboutUs: truncateString(salon.aboutUsFirst) })
	const keywords = formatMessage({ id: 'SEO - Salon detail page - meta keywords' }, { salonServices: salonServices.join(', ') })

	const ogTitle = formatMessage({ id: 'SEO - Salon detail page - meta og title' }, { salonName: salon.name, salonCity: salon.address?.city ?? '' })
	const ogDescription = formatMessage({ id: 'SEO - Salon detail page - meta og description' }, { salonAboutUs: truncateString(salon.aboutUsFirst) })

	const salonImage = salon.images[0]?.resizedImages.small || `${assetsPath}/${require('../../../../assets/images/basic-salon-placeholder-small.jpg')}`

	let robots = null

	if (salon.isPrivate) {
		robots = <meta key='robots' name='robots' content='noindex, nofollow' />
	} else if (salonNonHomeShopView) {
		robots = <meta key='robots' name='robots' content='noindex, follow' />
	}

	useEffect(() => {
		document.title = pageTitle
	}, [pageTitle])

	// NOTE: defining children as array and using .flat() on it otherwise <Head> doesn't work properly
	// each item has to have unique 'key' because <Head/> iterates over the array
	const children = [
		// charset
		<meta key='charset' charSet='utf-8' />,

		// title
		<title key='title'>{pageTitle}</title>,

		// description
		<meta key='description' name='description' content={description} />,

		// keywords
		<meta key='keywords' name='keywords' content={keywords} />,

		// canonical
		<link key='canonical' rel='canonical' href={currentPath} />,

		// cross-country views are reachable for users but must not be indexed
		robots,

		// OG tags (social networks) — reflect the URL the user is actually on
		<meta key='og:title' property='og:title' content={ogTitle} />,
		<meta key='og:description' property='og:description' content={ogDescription} />,
		<meta key='og:url' property='og:url' content={currentPath} />,
		<meta key='og:image' property='og:image' content={salonImage} />
	]
		.flat()
		.filter(Boolean)

	return <Head>{children}</Head>
}

export default SalonHead
